import { useCallback, useContext, useMemo, useState } from 'react';
// Resolves to the embedded flow's synthetic location when the page that
// composes this hook is rendered inside a panel.
import { useRouteLocation as useLocation } from '../../../../common/embeddedFlow/embeddedRoute';
import { parseLocalDate } from '../../../../utils/formatLocalDate';
import { ApiContext } from '../../../../context/MyContext';
import {
  fetchWorkoutTemplateSummaries,
  modifyTemplate,
  saveTemplateName,
  getWorkoutEventTypeId,
} from '../../core/api/workoutApi';
import { useWorkoutData } from '../../core/providers/WorkoutDataProvider';
import { useUtils } from '../../../../context/UtilsContext';
import { useCachedList } from '../../../../hooks/useCachedList';
import { workoutTemplatesCache } from '../../../../utils/listCache/caches';

export function formatWorkoutCreatedDate(value) {
  if (!value) return '-';
  const iso = parseLocalDate(value);
  if (iso) return iso.toLocaleDateString();
  const match = String(value).trim().match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (!match) return '-';
  const date = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
}

export function useWorkoutTemplateList() {
  const location = useLocation();
  const { get, post } = useContext(ApiContext);
  const { workoutCommonUtils } = useWorkoutData();
  const { sessionUtils, utilsLoading: sessionUtilsLoading } = useUtils();
  const eventTypeId = getWorkoutEventTypeId(sessionUtils);
  const [searchTerm, setSearchTerm] = useState('');

  const fetcher = useCallback(async () => {
    const rows = await fetchWorkoutTemplateSummaries(get, sessionUtils);
    // Normalize before caching so cached rows are usable as-is on the next paint.
    return (Array.isArray(rows) ? rows : []).map((t) => ({
      ...t,
      id: t.id ?? t.template_id,
    }));
  }, [get, sessionUtils]);

  const {
    data: cachedTemplates,
    loading: templatesLoading,
    isRevalidating,
    isRefreshing,
    refresh,
    mutate,
  } = useCachedList({
    cache: workoutTemplatesCache,
    key: eventTypeId ?? null,
    fetcher,
    enabled: Boolean(eventTypeId),
  });

  const templates = useMemo(() => cachedTemplates ?? [], [cachedTemplates]);
  // Nothing to fetch until the session resolves the event type — stay in the
  // loading state rather than flashing "no templates". Once session utils have
  // settled without an event type, fall through so the empty state can show.
  const loading =
    templatesLoading ||
    (!eventTypeId && sessionUtilsLoading && cachedTemplates === undefined);

  const fromUserPage = location.state?.fromUserPage || false;
  const fromFitness = location.state?.fromFitness || false;
  const inAssignFlow = fromUserPage || fromFitness;
  const clientId = location.state?.clientId;
  const returnTo = location.state?.returnTo;
  const assignToWeekday = location.state?.assignToWeekday;
  const durationId = location.state?.durationId;

  const fromDate = useMemo(
    () =>
      location.state?.fromDate instanceof Date
        ? location.state.fromDate
        : location.state?.fromDate
          ? new Date(location.state.fromDate)
          : new Date(),
    [location.state?.fromDate]
  );
  const toDate = useMemo(
    () =>
      location.state?.toDate instanceof Date
        ? location.state.toDate
        : location.state?.toDate
          ? new Date(location.state.toDate)
          : (() => {
              const d = new Date();
              d.setDate(d.getDate() + 7);
              return d;
            })(),
    [location.state?.toDate]
  );

  const listState = useMemo(
    () => ({
      fromFitness,
      fromDate,
      toDate,
      assignToWeekday,
      durationId,
      ...(fromUserPage && { fromUserPage: true, clientId, returnTo }),
    }),
    [
      fromFitness,
      fromDate,
      toDate,
      fromUserPage,
      clientId,
      returnTo,
      assignToWeekday,
      durationId,
    ]
  );

  const filteredTemplates = useMemo(() => {
    const visible = templates.filter((t) => (t.template_id ?? t.id) !== 0);
    const q = searchTerm.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter((t) => (t.title || '').toLowerCase().includes(q));
  }, [templates, searchTerm]);

  const removeTemplateLocally = useCallback(
    (templateId) => {
      // Callers drop the row optimistically; the next mount's revalidation
      // confirms it against the server.
      mutate((prev) => prev.filter((t) => t.id !== templateId));
    },
    [mutate]
  );

  const deleteTemplate = useCallback(
    async (templateId) => {
      try {
        const response = await modifyTemplate(post, sessionUtils, templateId, 'DELETE');
        if (response?.status === 200) return { success: true };
        return { success: false, message: 'Failed to delete the template.' };
      } catch (error) {
        console.error('Error deleting workout template:', error);
        return { success: false, message: 'An error occurred while deleting the template.' };
      }
    },
    [post, sessionUtils]
  );

  const cloneTemplate = useCallback(
    async ({ id, title, description, security_id }) => {
      const eventTypes = sessionUtils?.event_types?.[0]?.event_types || [];
      const templateEventId = getWorkoutEventTypeId(sessionUtils);

      try {
        const response = await saveTemplateName(
          post,
          {
            event_type_id: templateEventId,
            templates: [{ operation: 'CLONE', id, title, description, security_id }],
          },
          workoutCommonUtils?.workout_group_types || [],
          eventTypes
        );
        const newId = response?.data?.data?.templates?.[0]?.id;
        if (newId) {
          // Refetch in the background so the clone is already in the cached
          // list when the user returns; current rows stay on screen.
          refresh().catch(() => {});
          return { success: true, id: newId };
        }
        return { success: false, message: 'Cloned template ID not found.' };
      } catch (error) {
        console.error('Error cloning workout template:', error);
        return { success: false, message: error.message || 'Failed to clone the template.' };
      }
    },
    [post, sessionUtils, workoutCommonUtils, refresh]
  );

  const getSecurityId = useCallback(
    (securityName) => {
      if (!workoutCommonUtils || !securityName) return '';
      return (
        workoutCommonUtils.securities?.find(
          (s) => s.description.toLowerCase() === securityName.toLowerCase()
        )?.id || ''
      );
    },
    [workoutCommonUtils]
  );

  return {
    templates: filteredTemplates,
    allTemplates: templates,
    loading,
    isRevalidating,
    isRefreshing,
    refresh,
    searchTerm,
    setSearchTerm,
    workoutCommonUtils,
    fromUserPage,
    fromFitness,
    inAssignFlow,
    listState,
    removeTemplateLocally,
    deleteTemplate,
    cloneTemplate,
    getSecurityId,
  };
}
